import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, Filter, Layout, Eye, Check, X, ChevronRight,
  Building2, GraduationCap, Briefcase, HeartPulse, Dumbbell,
  TrendingUp, ShoppingCart, Palette, CalendarDays, Store,
  Sparkles, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category Icons
const CATEGORY_ICONS = {
  real_estate: Building2,
  education: GraduationCap,
  saas: Briefcase,
  healthcare: HeartPulse,
  fitness: Dumbbell,
  finance: TrendingUp,
  ecommerce: ShoppingCart,
  agency: Palette,
  events: CalendarDays,
  local: Store
};

// Category Colors
const CATEGORY_COLORS = {
  real_estate: 'from-green-500 to-emerald-600',
  education: 'from-purple-500 to-violet-600',
  saas: 'from-blue-500 to-indigo-600',
  healthcare: 'from-cyan-500 to-teal-600',
  fitness: 'from-red-500 to-rose-600',
  finance: 'from-amber-500 to-orange-600',
  ecommerce: 'from-pink-500 to-rose-600',
  agency: 'from-fuchsia-500 to-purple-600',
  events: 'from-yellow-500 to-amber-600',
  local: 'from-lime-500 to-green-600'
};

export default function TemplateMarketplace({ onSelectTemplate, onClose }) {
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const token = localStorage.getItem('auth_token');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [catRes, tempRes] = await Promise.all([
        fetch(`${API_URL}/api/marketplace/categories`),
        fetch(`${API_URL}/api/marketplace/templates`)
      ]);

      if (catRes.ok) {
        const data = await catRes.json();
        setCategories(data.categories || []);
      }

      if (tempRes.ok) {
        const data = await tempRes.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Failed to fetch marketplace data:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplatesByCategory = async (categoryId) => {
    try {
      const res = await fetch(`${API_URL}/api/marketplace/templates?category=${categoryId}`);
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const handleCategorySelect = (categoryId) => {
    if (selectedCategory === categoryId) {
      setSelectedCategory(null);
      fetchData();
    } else {
      setSelectedCategory(categoryId);
      fetchTemplatesByCategory(categoryId);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      fetchData();
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/marketplace/templates?search=${encodeURIComponent(searchTerm)}`);
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  const handleSelectTemplate = async (templateId) => {
    try {
      const res = await fetch(`${API_URL}/api/marketplace/templates/${templateId}`);
      if (res.ok) {
        const template = await res.json();
        onSelectTemplate?.(template);
        toast.success(`Selected "${template.name}" template`);
      }
    } catch (error) {
      toast.error('Failed to load template');
    }
  };

  const handlePreview = async (templateId) => {
    try {
      const res = await fetch(`${API_URL}/api/marketplace/templates/${templateId}`);
      if (res.ok) {
        const template = await res.json();
        setPreviewTemplate(template);
      }
    } catch (error) {
      toast.error('Failed to load preview');
    }
  };

  const filteredTemplates = templates.filter(t => 
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 20 }}
        className="bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 border border-emerald-800/30 rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-emerald-800/30 bg-gradient-to-r from-emerald-900/20 to-transparent">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Template Marketplace</h2>
                <p className="text-emerald-300/70 text-sm">Choose from 15+ professionally designed templates</p>
              </div>
            </div>
            <Button variant="ghost" onClick={onClose} className="text-emerald-300/70 hover:text-white">
              <X className="w-6 h-6" />
            </Button>
          </div>

          {/* Search */}
          <div className="relative max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-300/50" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search templates..."
              className="w-full pl-12 pr-4 py-3 bg-slate-800/50 border border-emerald-800/30 rounded-xl text-white placeholder-emerald-300/50 focus:outline-none focus:border-emerald-500/50"
            />
          </div>
        </div>

        <div className="flex h-[calc(90vh-180px)]">
          {/* Categories Sidebar */}
          <div className="w-64 border-r border-emerald-800/30 p-4 overflow-y-auto bg-slate-900/50">
            <h3 className="text-sm font-semibold text-emerald-300/50 uppercase tracking-wider mb-4">
              Categories
            </h3>
            <div className="space-y-2">
              {categories.map(category => {
                const Icon = CATEGORY_ICONS[category.id] || Layout;
                const isSelected = selectedCategory === category.id;
                const gradientClass = CATEGORY_COLORS[category.id] || 'from-gray-500 to-gray-600';
                
                return (
                  <button
                    key={category.id}
                    onClick={() => handleCategorySelect(category.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                      isSelected 
                        ? `bg-gradient-to-r ${gradientClass} text-white shadow-lg` 
                        : 'text-emerald-300/70 hover:bg-slate-800/50 hover:text-white'
                    }`}
                  >
                    <div className={`p-2 rounded-lg ${isSelected ? 'bg-white/20' : 'bg-slate-800'}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-medium text-sm">{category.name}</p>
                      <p className={`text-xs ${isSelected ? 'text-white/70' : 'text-emerald-300/50'}`}>
                        {category.description}
                      </p>
                    </div>
                    {isSelected && <Check className="w-4 h-4" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Templates Grid */}
          <div className="flex-1 p-6 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <Layout className="w-16 h-16 text-emerald-800/50 mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">No templates found</h3>
                <p className="text-emerald-300/70">Try a different search or category</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-6">
                {filteredTemplates.map(template => {
                  const gradientClass = CATEGORY_COLORS[template.category] || 'from-gray-500 to-gray-600';
                  
                  return (
                    <motion.div
                      key={template.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="group"
                    >
                      <Card className="bg-slate-800/50 border-emerald-800/30 overflow-hidden hover:border-emerald-500/50 transition-all hover:shadow-xl hover:shadow-emerald-500/10">
                        {/* Preview Image */}
                        <div className="relative h-48 overflow-hidden">
                          <img
                            src={template.preview_image}
                            alt={template.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                          
                          {/* Category Badge */}
                          <div className={`absolute top-3 left-3 px-2 py-1 rounded-lg bg-gradient-to-r ${gradientClass} text-white text-xs font-medium`}>
                            {template.category.replace('_', ' ')}
                          </div>
                          
                          {/* Popularity */}
                          <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-lg bg-black/50 text-amber-400 text-xs">
                            <Star className="w-3 h-3 fill-current" />
                            {template.popularity}%
                          </div>

                          {/* Hover Actions */}
                          <div className="absolute inset-0 flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity bg-black/40">
                            <Button
                              size="sm"
                              onClick={() => handlePreview(template.id)}
                              className="bg-white/20 hover:bg-white/30 backdrop-blur-sm"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Preview
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleSelectTemplate(template.id)}
                              className="bg-emerald-600 hover:bg-emerald-500"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Use
                            </Button>
                          </div>
                        </div>

                        <CardContent className="p-4">
                          <h3 className="font-semibold text-white group-hover:text-emerald-400 transition-colors">
                            {template.name}
                          </h3>
                          <p className="text-sm text-emerald-300/70 mt-1 line-clamp-2">
                            {template.description}
                          </p>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Preview Modal */}
      <AnimatePresence>
        {previewTemplate && (
          <TemplatePreviewModal
            template={previewTemplate}
            onClose={() => setPreviewTemplate(null)}
            onSelect={() => {
              onSelectTemplate?.(previewTemplate);
              setPreviewTemplate(null);
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// Template Preview Modal
function TemplatePreviewModal({ template, onClose, onSelect }) {
  const [activePageIndex, setActivePageIndex] = useState(0);
  const page = template.pages?.[activePageIndex];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-8"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-slate-900 border border-emerald-800/30 rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-emerald-800/30 flex items-center justify-between bg-slate-800/50">
          <div>
            <h3 className="text-lg font-semibold text-white">{template.name}</h3>
            <p className="text-sm text-emerald-300/70">{template.description}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={onSelect} className="bg-emerald-600 hover:bg-emerald-500">
              <Check className="w-4 h-4 mr-2" />
              Use This Template
            </Button>
            <Button variant="ghost" onClick={onClose} className="text-emerald-300/70">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Page Tabs */}
        {template.pages?.length > 1 && (
          <div className="px-4 py-2 border-b border-emerald-800/30 flex gap-2">
            {template.pages.map((p, idx) => (
              <button
                key={idx}
                onClick={() => setActivePageIndex(idx)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activePageIndex === idx
                    ? 'bg-emerald-600 text-white'
                    : 'text-emerald-300/70 hover:bg-slate-800'
                }`}
              >
                {p.name}
              </button>
            ))}
          </div>
        )}

        {/* Preview Content */}
        <div 
          className="overflow-y-auto max-h-[calc(90vh-150px)]"
          style={{ backgroundColor: template.theme?.background_color || '#0f172a' }}
        >
          {page?.components?.map((component, idx) => (
            <PreviewComponent 
              key={idx} 
              component={component} 
              theme={template.theme}
            />
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}

// Preview Component Renderer
function PreviewComponent({ component, theme }) {
  const props = component.props || {};
  const primaryColor = theme?.primary_color || '#059669';

  switch (component.type) {
    case 'hero':
      return (
        <div 
          className="py-20 px-8 text-center"
          style={{ 
            backgroundImage: props.backgroundImage 
              ? `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${props.backgroundImage})` 
              : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          {props.badge && (
            <span className="inline-block px-4 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-sm text-white mb-6">
              {props.badge}
            </span>
          )}
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 max-w-3xl mx-auto">
            {props.headline || 'Your Headline'}
          </h1>
          <p className="text-xl text-white/70 mb-8 max-w-2xl mx-auto">
            {props.subheadline || 'Your subheadline'}
          </p>
          <button 
            className="px-8 py-4 rounded-xl font-semibold text-white text-lg shadow-lg"
            style={{ backgroundColor: primaryColor }}
          >
            {props.buttonText || 'Get Started'}
          </button>
        </div>
      );

    case 'features':
      const features = props.features || [];
      return (
        <div className="py-16 px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            {props.title || 'Features'}
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {features.map((f, i) => (
              <div key={i} className="text-center p-6 rounded-xl bg-white/5">
                <div className="text-4xl mb-4">{f.icon}</div>
                <h3 className="text-lg font-semibold text-white mb-2">{f.title}</h3>
                <p className="text-white/60">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      );

    case 'form':
      const fields = props.fields || [];
      return (
        <div className="py-16 px-8 max-w-md mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-2">
            {props.title || 'Contact Us'}
          </h2>
          {props.subtitle && (
            <p className="text-white/60 text-center mb-8">{props.subtitle}</p>
          )}
          <div className="space-y-4">
            {fields.map((f, i) => (
              <div key={i}>
                <label className="block text-sm text-white/70 mb-2">{f.label}</label>
                <input
                  type={f.type}
                  placeholder={f.label}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40"
                  disabled
                />
              </div>
            ))}
            <button 
              className="w-full py-4 rounded-xl font-semibold text-white mt-4"
              style={{ backgroundColor: primaryColor }}
            >
              {props.buttonText || 'Submit'}
            </button>
          </div>
        </div>
      );

    case 'testimonial':
      const testimonials = props.testimonials || [];
      return (
        <div className="py-16 px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            {props.title || 'Testimonials'}
          </h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {testimonials.map((t, i) => (
              <div key={i} className="p-6 rounded-xl bg-white/5">
                <div className="flex text-amber-400 mb-4">
                  {[...Array(t.rating || 5)].map((_, j) => (
                    <Star key={j} className="w-5 h-5 fill-current" />
                  ))}
                </div>
                <p className="text-white/80 italic mb-4">"{t.text}"</p>
                <div>
                  <p className="font-semibold text-white">{t.name}</p>
                  <p className="text-sm text-white/50">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      );

    case 'pricing':
      return (
        <div className="py-16 px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {props.title || 'Pricing'}
          </h2>
          <div className="flex items-baseline justify-center gap-2 mb-4">
            <span className="text-5xl font-bold text-white">{props.price || '₹999'}</span>
            {props.originalPrice && (
              <span className="text-xl text-white/40 line-through">{props.originalPrice}</span>
            )}
          </div>
          {props.discount && (
            <span className="inline-block px-4 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: primaryColor, color: 'white' }}>
              {props.discount}
            </span>
          )}
        </div>
      );

    case 'countdown':
      return (
        <div className="py-12 px-8 text-center">
          <h3 className="text-xl font-semibold text-white mb-6">{props.title || 'Offer Ends In'}</h3>
          <div className="flex justify-center gap-4">
            {['Days', 'Hours', 'Mins', 'Secs'].map((unit, i) => (
              <div key={i} className="bg-white/10 px-6 py-4 rounded-xl">
                <div className="text-3xl font-bold text-white">00</div>
                <div className="text-xs text-white/50 mt-1">{unit}</div>
              </div>
            ))}
          </div>
        </div>
      );

    default:
      return (
        <div className="py-8 px-8 text-center text-white/50">
          {component.type} component
        </div>
      );
  }
}
